import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Question } from '../../interfaces/questions.interface';
import { getInitialValues } from './QuestionForm/initialValues';

type QuestionFormData = Omit<Question, '_id' | 'chapter'>;

interface QuestionFormProps {
  onSubmit: (question: QuestionFormData) => void;
  category: string;
  initialValues?: QuestionFormData;
  submitLabel?: string;
}

const QuestionForm: React.FC<QuestionFormProps> = ({ 
  onSubmit, 
  category, 
  initialValues,
  submitLabel = "Ajouter la question"
}) => {
  const [formData, setFormData] = useState<QuestionFormData>(() => 
    initialValues || getInitialValues(category)
  );

  useEffect(() => {
    if (initialValues) {
      setFormData(initialValues);
    }
  }, [initialValues]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCorrectAnswerToggle = (answer: string) => {
    setFormData(prev => {
      const currentAnswers = prev.correctAnswers;
      const newAnswers = currentAnswers.includes(answer)
        ? currentAnswers.filter(a => a !== answer)
        : [...currentAnswers, answer];
      
      return {
        ...prev,
        correctAnswers: newAnswers
      };
    });
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    onSubmit(formData);
    if (!initialValues) {
      setFormData(getInitialValues(category)); // Only reset if not in edit mode
    }
  };

  return (
    <div className="space-y-4 font-sans">
      <div className="field">
        <label htmlFor="questionAnnouncement" className="block text-sm font-medium text-gray-700 mb-1">
          Énoncé de la question
        </label>
        <InputTextarea
          id="questionAnnouncement"
          name="questionAnnouncement"
          value={formData.questionAnnouncement}
          onChange={handleInputChange}
          required
          rows={2}
          className="w-full"
          autoResize
        />
      </div>

      {['A', 'B', 'C', 'D'].map((letter) => (
        <div key={letter} className="field flex align-start justify-start gap-4 font-sans">
          <div className="flex-1">
            <label htmlFor={`answer${letter}`} className="block text-sm font-medium text-gray-700 mb-1">
              Réponse {letter}
            </label>
            <InputText
              id={`answer${letter}`}
              name={`answer${letter}`}
              value={formData[`answer${letter}` as keyof QuestionFormData]}
              onChange={handleInputChange}
              required
              className="w-full"
            />
          </div>
          <div className="flex items-center mt-5">
            <Checkbox
              inputId={`correct${letter}`}
              checked={formData.correctAnswers.includes(letter)}
              onChange={() => handleCorrectAnswerToggle(letter)}
              className='focus:ring-0 active:ring-0 focus:outline-0 active:outline-0'
            />
            <label htmlFor={`correct${letter}`} className="ml-2 text-sm text-gray-600 h-fit">
              Correcte
            </label>
          </div>
        </div>
      ))}

      <div className="flex justify-end">
        <Button
          type="button"
          icon={initialValues ? "pi pi-check" : "pi pi-plus"}
          label={submitLabel}
          disabled={!formData.questionAnnouncement || !formData.correctAnswers.length}
          onClick={handleSubmit}
          className="bg-primary border-0 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
        />
      </div>
    </div>
  );
};

export default QuestionForm;
